import { ResponsiveValue } from "@iunctim/utility-frontend/Resources/Public/Scripts/utilities/ResponsiveValue.js";
import { Header } from "./abstractions/Header.js";

/**
 * Class to implement the responsive dropdown-menu
 * functionality for a single instance; the application
 * is done via the ResponsiveDropdownMenuManager class
 */
export class Default extends Header {
    private static instance: Default;

    private mainReductable: HTMLElement | null = null;

    private lastScrollY: number = 0;
    private ticking: boolean = false;
    private behaviour: string = "static";

    private reductiveThreshold: number = 0;
    private reductiveRestoreThreshold: number = 0;

    /**
     * The minimum hysteresis (in pixels) for the scroll
     * trigger that influences the reduction of the header
     * height and the visibility of the header
     */
    private static readonly MINIMUM_HYSTERESIS: number = 10;

    /**
     * The minimum threshold (in pixels) for the scroll
     * trigger to restore the header before it reaches
     * the top of the page
     */
    private static readonly MINIMUM_RESTORE_THRESHOLD: number = 150;

    /**
     * The heights of the main-section;
     * both for original and reduced height
     */
    private static readonly MAIN_SECTION_HEIGHTS = new ResponsiveValue<{
        reduce: number;
        restore: number;
    }>({
        0: {
            reduce: 64,
            restore: 100,
        },
        640: {
            reduce: 72,
            restore: 96,
        },
        1024: {
            reduce: 80,
            restore: 112,
        },
    });

    /**
     * The private constructor
     * prevents direct instantiation
     */
    private constructor() {
        super();

        this.header = document.querySelector(
            `header[scope="header"][template="default"]`,
        );
        if (!this.header) return;

        // Find the main reductable element and prepare it's animation
        if (this.header?.getAttribute("reduction")?.includes("main")) {
            this.mainReductable = this.header.querySelector(
                `[reduction-target="main"]`,
            );
            if (this.mainReductable) {
                const height = Default.MAIN_SECTION_HEIGHTS.get()!!.restore;
                this.mainReductable.style.height = `${height}px`;
            }
        }

        this.evaluateReductiveThresholds();

        this.applyBehaviour();

        this.observeMutations();
        this.observeResizings();
    }

    /**
     * Method to instantiate the
     * header on the current page
     *
     * @returns {Default}
     */
    public static use(): Default {
        if (!Default.instance) {
            Default.instance = new Default();
        }
        return Default.instance;
    }

    /**
     * Method to evaluate the reductive thresholds
     *
     * @returns {void}
     */
    private evaluateReductiveThresholds(): void {
        // Select the reductable elements
        const selectors =
            this.header
                ?.getAttribute("reduction")
                ?.split(",")
                ?.map((target) => `[reduction-target="${target}"]`) ?? [];

        if (!selectors.length) selectors.push(`[reduction-target]`);
        const reductableElements =
            this.header?.querySelectorAll(selectors.join(",")) ?? [];

        // Evaluate the reductable height by accumulating the height of those elements
        const reductableHeight = [...reductableElements].reduce(
            (height, target) => {
                switch (target.getAttribute("reductable-target")) {
                    case "main":
                        return (
                            height + Default.MAIN_SECTION_HEIGHTS.get()!!.reduce
                        );
                    default:
                        return height + target.clientHeight;
                }
            },
            Default.MINIMUM_HYSTERESIS,
        );

        // Define the reductive thresholds for initialization and reset
        if (
            Default.MINIMUM_RESTORE_THRESHOLD + reductableHeight >
            this.reductiveThreshold
        ) {
            this.reductiveThreshold =
                Default.MINIMUM_RESTORE_THRESHOLD + reductableHeight;
        }
        this.reductiveRestoreThreshold =
            this.reductiveThreshold - reductableHeight;
    }

    /**
     * Method to apply the defined behaviour
     *
     * @returns {void}
     */
    private applyBehaviour(): void {
        if (!this.header) return;
        this.behaviour = this.header.getAttribute("behaviour") || "static";
        document.body.style.setProperty("--header-behaviour", this.behaviour);

        this.header.style = "";
        this.header.removeAttribute("state");
        window.removeEventListener("scroll", this.onScroll);

        switch (this.behaviour) {
            case "sticky":
                this.header.setAttribute("state", "initial");
                window.addEventListener("scroll", this.onScroll, {
                    passive: true,
                });
                break;
            case "reveal":
                this.showHeader();
                this.header.setAttribute("state", "initial");

                this.lastScrollY = window.scrollY;
                window.addEventListener("scroll", this.onScroll, {
                    passive: true,
                });
                break;
        }
    }

    /**
     * Callback to handle the scroll event
     *
     * @returns {void}
     */
    private onScroll = (): void => {
        if (!this.header) return;

        if (!this.ticking) {
            window.requestAnimationFrame(this.handleScroll);
            this.ticking = true;
        }
    };

    /**
     * Callback to handle the animation frame request
     *
     * @returns {void}
     */
    private handleScroll = (): void => {
        if (!this.header) return;
        const currentScrollY = window.scrollY;

        // Visibility
        switch (this.behaviour) {
            case "reveal":
                if (
                    currentScrollY < this.reductiveThreshold ||
                    currentScrollY <
                        this.lastScrollY - Default.MINIMUM_HYSTERESIS
                ) {
                    this.showHeader();
                } else if (currentScrollY > this.lastScrollY) {
                    this.hideHeader();
                }
                break;
        }

        // Reduction
        switch (this.behaviour) {
            case "sticky":
            case "reveal":
                if (
                    this.header.getAttribute("state") !== "reduced" &&
                    currentScrollY > this.reductiveThreshold
                ) {
                    this.header.setAttribute("state", "reduced");
                    this.animateMainReductable("reduce");
                } else if (
                    this.header.getAttribute("state") !== "initial" &&
                    currentScrollY < this.reductiveRestoreThreshold
                ) {
                    this.header.setAttribute("state", "initial");
                    this.animateMainReductable("restore");
                }
                break;
        }

        this.lastScrollY = currentScrollY;
        this.ticking = false;
    };

    /**
     * Method to animate the main reductable element's
     * height based on the current viewport width
     *
     * @param {"reduce" | "restore"} action
     * @returns {void}
     */
    private animateMainReductable(action: "reduce" | "restore"): void {
        if (!this.mainReductable) return;
        const height = Default.MAIN_SECTION_HEIGHTS.get()!![action];
        this.mainReductable.style.height = `${height}px`;
    }

    /**
     * Method to register a mutation observer in
     * order to handle any changes to the behaviour
     *
     * @returns {void}
     */
    private observeMutations(): void {
        if (!this.header) return;

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (
                    mutation.type === "attributes" &&
                    mutation.attributeName === "behaviour"
                ) {
                    this.applyBehaviour();
                }
            });
        });

        observer.observe(this.header, {
            attributes: true,
        });
    }

    /**
     * Method to show the header
     *
     * @returns {void}
     */
    private showHeader(): void {
        if (!this.header) return;
        document.body.style.setProperty("--header-offset", "0px");
    }

    /**
     * Method to hide the header
     *
     * @returns {void}
     */
    private hideHeader(): void {
        if (!this.header) return;

        const offset = this.header.offsetHeight + Header.DROP_SHADOW_HEIGHT;

        document.body.style.setProperty("--header-offset", `${offset}px`);
    }
}
